import { atom } from 'nanostores';

/**
 * One brand-run action, in flight between the page banner and the wizard.
 *
 * The banner moved ABOVE the Design Studio masthead (both pages), so it no
 * longer renders inside the wizard that owns the run's transitions — the two
 * are in different subtrees, and on the Files page the wizard isn't mounted at
 * all. This atom is the hand-off: the banner REQUESTS an action, the wizard
 * (mounted on the Styles page whenever there is a draft) consumes it and
 * performs it against the run it already owns. From the Files page the banner
 * also navigates to Styles, so the intent is picked up as the wizard mounts.
 *
 * Feature-local on purpose (not `stores/`): the pair of surfaces that speak
 * through it both live in this feature.
 *
 * The `nonce` makes two requests of the same kind distinguishable, so a second
 * "Check again" is a new intent rather than an unchanged value the subscriber
 * would never see. The `workspaceId` is part of the intent for the same reason
 * the draft store is workspace-scoped: an in-session workspace switch between
 * the request and the consuming mount must not perform one workspace's action
 * against another's run.
 */

export type BrandRunIntentKind = 'enter' | 'retry' | 'restart' | 'check' | 'discard';

export type BrandRunIntent = { kind: BrandRunIntentKind; workspaceId: number; nonce: number };

export const $brandRunIntent = atom<BrandRunIntent | null>(null);

let lastNonce = 0;

/** Ask the wizard for an action on this workspace's tracked run. */
export function requestBrandRunIntent(kind: BrandRunIntentKind, workspaceId: number): void {
  lastNonce += 1;
  $brandRunIntent.set({ kind, workspaceId, nonce: lastNonce });
}

/**
 * Take the pending intent, clearing it. Callers consume BEFORE acting, so a
 * re-render during the action can't replay it.
 */
export function consumeBrandRunIntent(): BrandRunIntent | null {
  const intent = $brandRunIntent.get();
  if (intent != null) $brandRunIntent.set(null);
  return intent;
}
